import { Link } from "react-router-dom";
import {
  ArrowRightIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";
import {
  useDashboardCounters,
  useDashboardQueueSummary,
  useDashboardRecentAudit,
  useDashboardStorage,
  useDashboardSystem,
  useMe,
} from "@/api/hooks";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { formatBytes, relTime } from "@/lib/utils";
import type {
  DashboardRecentAudit,
  DashboardStorage,
  DashboardSystem,
} from "@/api/client";

export default function DashboardPage() {
  const me = useMe();
  const counters = useDashboardCounters();
  const storage = useDashboardStorage();
  const queue = useDashboardQueueSummary();
  const audit = useDashboardRecentAudit();
  const system = useDashboardSystem();

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-heading text-3xl font-light sm:text-4xl">
          <span className="text-bitrefinery-red">Dash</span>board
        </h1>
        <p className="mt-2 max-w-3xl text-sm text-bitrefinery-text text-body">
          Welcome back, <span className="font-mono">{me.data?.email}</span>. At-a-glance
          view of the mail system. Each tile refreshes independently; most are cached
          server-side (storage: 60s, queue: 15s, services: 30s, cert: 1h).
        </p>
      </div>

      <CountersRow
        data={counters.data}
        loading={counters.isLoading}
      />

      <div className="grid gap-4 lg:grid-cols-2">
        <StorageCard data={storage.data} loading={storage.isLoading} />
        <QueueCard data={queue.data} loading={queue.isLoading} />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <SystemCard data={system.data} loading={system.isLoading} />
        <AuditCard data={audit.data} loading={audit.isLoading} />
      </div>
    </div>
  );
}

/* -- counters row --------------------------------------------------------- */

function CountersRow({
  data,
  loading,
}: {
  data: {
    mailboxes: { active: number; disabled: number; total: number };
    aliases: number;
    domains: number;
    auditToday: number;
  } | undefined;
  loading: boolean;
}) {
  const items = [
    {
      label: "Mailboxes",
      value: data ? `${data.mailboxes.active}` : "—",
      sub: data ? `${data.mailboxes.disabled} disabled` : "",
      to: "/mailboxes",
    },
    {
      label: "Aliases",
      value: data ? `${data.aliases}` : "—",
      sub: "active",
      to: "/aliases",
    },
    {
      label: "Domains",
      value: data ? `${data.domains}` : "—",
      sub: "active",
      to: "/domains",
    },
    {
      label: "Audit events today",
      value: data ? `${data.auditToday}` : "—",
      sub: "since 00:00 local",
      to: "/audit",
    },
  ];
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
      {items.map((it) => (
        <Link
          key={it.label}
          to={it.to}
          className="group rounded-2xl border border-border bg-card px-4 py-3 shadow-sm transition-colors hover:border-bitrefinery-red/50"
        >
          <div className="text-xs uppercase tracking-wide text-muted-foreground">
            {it.label}
          </div>
          <div className="mt-1 flex items-baseline justify-between">
            <span className="text-3xl font-light tabular-nums">
              {loading ? "…" : it.value}
            </span>
            <ArrowRightIcon className="h-4 w-4 text-muted-foreground group-hover:text-bitrefinery-red" />
          </div>
          <div className="text-xs text-muted-foreground">{it.sub}</div>
        </Link>
      ))}
    </div>
  );
}

/* -- storage -------------------------------------------------------------- */

function StorageCard({
  data,
  loading,
}: {
  data: DashboardStorage | undefined;
  loading: boolean;
}) {
  const pct =
    data && data.totalQuotaBytes > 0
      ? Math.min(100, (data.totalUsedBytes / data.totalQuotaBytes) * 100)
      : null;
  const tone =
    pct === null ? "brand" : pct >= 90 ? "danger" : pct >= 70 ? "warn" : "brand";
  return (
    <Card>
      <CardHeader>
        <CardTitle>Storage</CardTitle>
        <Link to="/mailboxes" className="text-xs text-muted-foreground hover:underline">
          manage →
        </Link>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading && <p className="text-xs text-muted-foreground">Sampling doveadm…</p>}
        {data && (
          <>
            <div>
              <div className="flex items-baseline justify-between">
                <div>
                  <span className="text-2xl font-light tabular-nums">
                    {formatBytes(data.totalUsedBytes)}
                  </span>
                  <span className="text-sm text-muted-foreground">
                    {" "}
                    / {data.totalQuotaBytes > 0 ? formatBytes(data.totalQuotaBytes) : "unlimited"}
                  </span>
                </div>
                {pct !== null && (
                  <span className="font-mono text-xs text-muted-foreground">
                    {pct.toFixed(1)}%
                  </span>
                )}
              </div>
              {pct !== null && <Progress percent={pct} tone={tone} className="mt-2" />}
            </div>

            <div>
              <h4 className="mb-2 text-xs uppercase tracking-wide text-muted-foreground">
                Top by quota utilization
              </h4>
              {data.topByPercent.length === 0 ? (
                <p className="text-xs text-muted-foreground">
                  No quota-capped mailboxes with measurable usage yet.
                </p>
              ) : (
                <ul className="space-y-2">
                  {data.topByPercent.map((m) => {
                    const p = Math.min(100, m.percent ?? 0);
                    const t = p >= 95 ? "danger" : p >= 80 ? "warn" : "brand";
                    return (
                      <li key={m.address}>
                        <div className="flex items-baseline justify-between text-xs">
                          <span className="truncate font-mono">{m.address}</span>
                          <span className="text-muted-foreground">
                            {formatBytes(m.usedBytes)} / {formatBytes(m.quotaBytes)} (
                            {p.toFixed(0)}%)
                          </span>
                        </div>
                        <Progress percent={p} tone={t} className="mt-1" />
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

/* -- queue ---------------------------------------------------------------- */

function QueueCard({
  data,
  loading,
}: {
  data:
    | {
        total: number;
        active: number;
        deferred: number;
        held: number;
        oldestAgeSeconds: number;
      }
    | undefined;
  loading: boolean;
}) {
  const total = data?.total ?? 0;
  const oldest = data && data.oldestAgeSeconds > 0
    ? relTime(Math.floor(Date.now() / 1000) - data.oldestAgeSeconds)
    : "—";
  return (
    <Card>
      <CardHeader>
        <CardTitle>Queue</CardTitle>
        <Link to="/queue" className="text-xs text-muted-foreground hover:underline">
          inspect →
        </Link>
      </CardHeader>
      <CardContent>
        {loading && <p className="text-xs text-muted-foreground">Reading postqueue…</p>}
        {data && (
          <div className="grid grid-cols-4 gap-2">
            <QStat label="Total" value={total} />
            <QStat label="Active" value={data.active} />
            <QStat label="Deferred" value={data.deferred} tone={data.deferred > 0 ? "warn" : undefined} />
            <QStat label="Held" value={data.held} tone={data.held > 0 ? "danger" : undefined} />
          </div>
        )}
      </CardContent>
      <CardFooter>
        Oldest message age: <span className="ml-1 font-mono">{oldest}</span>
      </CardFooter>
    </Card>
  );
}

function QStat({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone?: "warn" | "danger";
}) {
  const color =
    tone === "danger"
      ? "text-rose-600"
      : tone === "warn"
        ? "text-amber-600"
        : "text-foreground";
  return (
    <div className="rounded-md bg-muted/50 px-3 py-2">
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div className={`text-xl font-light tabular-nums ${color}`}>{value}</div>
    </div>
  );
}

/* -- system health -------------------------------------------------------- */

function SystemCard({
  data,
  loading,
}: {
  data: DashboardSystem | undefined;
  loading: boolean;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>System health</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading && <p className="text-xs text-muted-foreground">Checking…</p>}
        {data && (
          <>
            <div>
              <h4 className="mb-2 text-xs uppercase tracking-wide text-muted-foreground">
                Services
              </h4>
              <ul className="grid grid-cols-2 gap-2">
                {data.services.map((s) => (
                  <li
                    key={s.name}
                    className="flex items-center justify-between rounded-md border bg-card px-2 py-1 text-xs"
                  >
                    <span className="font-mono">{s.name}</span>
                    <ServiceBadge active={s.active} state={s.state} />
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="mb-2 text-xs uppercase tracking-wide text-muted-foreground">
                TLS certificate
              </h4>
              {"ok" in data.cert ? (
                <CertLine notAfter={data.cert.notAfter} daysLeft={data.cert.daysLeft} />
              ) : (
                <p className="text-xs text-muted-foreground">
                  Cert check failed:{" "}
                  <span className="font-mono">{data.cert.error}</span>
                </p>
              )}
            </div>

            <div>
              <h4 className="mb-2 text-xs uppercase tracking-wide text-muted-foreground">
                rspamd
              </h4>
              {data.rspamd ? (
                <div className="grid grid-cols-3 gap-2 text-xs">
                  <RspamdStat label="Scanned" value={data.rspamd.scanned} />
                  <RspamdStat label="Ham" value={data.rspamd.ham_count} />
                  <RspamdStat label="Spam" value={data.rspamd.spam_count} />
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">
                  rspamd controller unreachable on 127.0.0.1:11334.
                </p>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function ServiceBadge({ active, state }: { active: boolean; state: string }) {
  if (active) {
    return (
      <Badge variant="secondary" className="gap-1">
        <CheckCircleIcon className="h-3 w-3" />
        active
      </Badge>
    );
  }
  return (
    <Badge variant="destructive" className="gap-1">
      <XCircleIcon className="h-3 w-3" />
      {state}
    </Badge>
  );
}

function CertLine({ notAfter, daysLeft }: { notAfter: string; daysLeft: number }) {
  const tone =
    daysLeft < 14
      ? { icon: XCircleIcon, color: "text-rose-600" }
      : daysLeft < 30
        ? { icon: ExclamationTriangleIcon, color: "text-amber-600" }
        : { icon: CheckCircleIcon, color: "text-emerald-600" };
  const Icon = tone.icon;
  return (
    <div className="flex items-center justify-between text-xs">
      <div className="flex items-center gap-2">
        <Icon className={`h-4 w-4 ${tone.color}`} />
        <span className="font-mono">{new Date(notAfter).toLocaleDateString()}</span>
      </div>
      <span className={`tabular-nums ${tone.color}`}>
        {daysLeft.toFixed(0)} days left
      </span>
    </div>
  );
}

function RspamdStat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-md bg-muted/50 px-2 py-1.5">
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div className="font-mono text-sm tabular-nums">{value.toLocaleString()}</div>
    </div>
  );
}

/* -- recent audit --------------------------------------------------------- */

function AuditCard({
  data,
  loading,
}: {
  data: DashboardRecentAudit | undefined;
  loading: boolean;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Recent activity</CardTitle>
        <Link to="/audit" className="text-xs text-muted-foreground hover:underline">
          full log →
        </Link>
      </CardHeader>
      <CardContent>
        {loading && <p className="text-xs text-muted-foreground">Loading…</p>}
        {data && data.entries.length === 0 && (
          <p className="text-xs text-muted-foreground">No activity yet.</p>
        )}
        {data && data.entries.length > 0 && (
          <ul className="space-y-2">
            {data.entries.map((e) => (
              <li key={e.id} className="text-xs">
                <div className="flex items-baseline justify-between gap-2">
                  <div className="min-w-0 flex-1 truncate">
                    <span className="font-mono">{e.admin_email}</span>{" "}
                    <span className="text-muted-foreground">{e.action}</span>
                    {e.target && (
                      <span className="ml-1 font-mono">{e.target}</span>
                    )}
                  </div>
                  <time className="whitespace-nowrap text-muted-foreground">
                    {new Date(e.at).toLocaleString()}
                  </time>
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
